import { Injectable, OnDestroy, inject } from '@angular/core';
import {
    ModelOrderProcessFilterInput,
    ModelOrderSubprocessFilterInput,
    OrderProcess
} from '@app/core/services/api/API.service';
import { BehaviorSubject } from 'rxjs';
import { sortJSON } from '../../util.service';
import { take } from 'rxjs/operators';
import * as subscriptions from '@app/core/graphql/subscriptions';
import {
    AmplifyApiService,
    OrderProcessService,
    OrderSubprocessService
} from '@app/core/api/services';

@Injectable({
    providedIn: 'root'
})
export class PremiumOrderProcessesService implements OnDestroy {
    private _premiumOrderProcesses$ = new BehaviorSubject<OrderProcess[]>([]);
    private _loading$ = new BehaviorSubject<boolean>(true);
    private premiumOrderProcesses: OrderProcess[] = [];
    private subscriptions: any[] = [];
    private _amplifyApiService = inject(AmplifyApiService);
    private _premiumOrderProcessService = inject(OrderProcessService);
    private _premiumOrderSubprocessService = inject(OrderSubprocessService);

    constructor() {
        this.subscriptions.push(
            this._amplifyApiService
                .subscribe({ query: subscriptions.onUpdateOrderProcess })
                .subscribe(observer => {
                    const data = observer.data.onUpdateOrderProcess;
                    this.premiumOrderProcesses = this.premiumOrderProcesses.map(
                        (premiumOrderProcess: OrderProcess) => {
                            return premiumOrderProcess.id === data.id
                                ? data
                                : premiumOrderProcess;
                        }
                    );
                    this.premiumOrderProcesses = sortJSON(
                        this.premiumOrderProcesses,
                        'position',
                        'asc'
                    );
                    this._premiumOrderProcesses$.next(
                        this.premiumOrderProcesses
                    );
                })
        );
    }

    ngOnDestroy(): void {
        this.subscriptions.forEach(subscription => subscription.unsubscribe());
    }

    get premiumOrderProcesses$() {
        return this._premiumOrderProcesses$.asObservable();
    }

    get loading$() {
        return this._loading$.asObservable();
    }

    async listPremiumOrderProcesses() {
        try {
            this._loading$.next(true);
            this.premiumOrderProcesses = [];
            this.premiumOrderProcesses = await this.listPremiumOrderProcessesDB();
        } catch (err) {
            console.log(err);
        } finally {
            this._premiumOrderProcesses$.next(this.premiumOrderProcesses);
            this._loading$.next(false);
        }
    }

    async findPremiumOrderProcessById(
        id: string
    ): Promise<OrderProcess | undefined> {
        try {
            const premiumOrderProcesses = await this.premiumOrderProcesses$
                .pipe(take(1))
                .toPromise(); // Take 1 to unsubscribe after getting the value
            return premiumOrderProcesses.find(
                premiumOrderProcess => premiumOrderProcess.id === id
            );
        } catch (error) {
            console.error('Error finding premium order process:', error);
            return undefined;
        }
    }

    async findPremiumOrderProcessesByClientId(
        clientId: string
    ): Promise<OrderProcess[] | undefined> {
        try {
            const premiumOrderProcesses = await this.premiumOrderProcesses$
                .pipe(take(1))
                .toPromise(); // Take 1 to unsubscribe after getting the value
            return sortJSON(
                premiumOrderProcesses.filter(
                    premiumOrderProcess =>
                        premiumOrderProcess.clientId === clientId
                ),
                'position',
                'asc'
            );
        } catch (error) {
            console.error('Error finding premium order process:', error);
            return undefined;
        }
    }

    async getPremiumOrderProcessesByClientId(
        clientId: string,
        filter: ModelOrderProcessFilterInput = null,
        filterSP: ModelOrderSubprocessFilterInput = null
    ): Promise<OrderProcess[] | undefined> {
        try {
            const promisesSubprocesses = [];
            let premiumOrderProcesses: OrderProcess[] = sortJSON(
                await this._premiumOrderProcessService.premiumProcessByClient(
                    clientId,
                    filter
                ),
                'position',
                'asc'
            );
            for (const premiumOrderProcess of premiumOrderProcesses) {
                promisesSubprocesses.push(
                    this._premiumOrderSubprocessService.listPremiumOrderSubprocessByProcessId(
                        premiumOrderProcess.id,
                        filterSP
                    )
                );
            }
            const subprocessesDB = await Promise.all(promisesSubprocesses);
            premiumOrderProcesses = premiumOrderProcesses.map(
                (premiumOrderProcess: OrderProcess, index: number) => {
                    premiumOrderProcess.subprocesses.items = sortJSON(
                        subprocessesDB[index],
                        'position',
                        'asc'
                    );
                    return premiumOrderProcess;
                }
            );
            return premiumOrderProcesses;
        } catch (error) {
            console.error('Error finding premium order process:', error);
            return undefined;
        }
    }

    private async listPremiumOrderProcessesDB(): Promise<OrderProcess[]> {
        const result = await this._premiumOrderProcessService.listPremiumOrderProcesses();
        const items: OrderProcess[] = sortJSON(result, 'position', 'asc');
        return items;
    }
}
